import { html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { PageElement } from "../lib/page-element.js";
import { shared } from "../styles.js";
import { myTeams, createTeam } from "../api/teams.js";
import { humanError } from "../api/errors.js";
import { tokenError } from "../lib/grammar.js";
import { navigate } from "../nav.js";
import { ROLE_LABELS } from "../lib/roles.js";
import type { TeamMemberRecord } from "../api/registry.js";

/**
 * page-teams lists the current user's team memberships (personal team included)
 * and hosts the create-team form. Creating a team also claims the same-named
 * scope; the creator becomes its owner.
 */
@customElement("page-teams")
export class PageTeams extends PageElement {
  static override styles = [
    shared,
    css`
      .two {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: var(--sp-3);
        align-items: end;
      }
      td .mono {
        color: var(--text-faint);
        font-size: var(--fs-1);
      }
      @media (max-width: 560px) {
        .two {
          grid-template-columns: 1fr;
        }
      }
    `,
  ];

  @state() private memberships: TeamMemberRecord[] = [];
  @state() private errorMsg = "";
  @state() private newName = "";
  @state() private newDisplay = "";
  @state() private creating = false;

  override connectedCallback() {
    super.connectedCallback();
    this.setTitle("Teams");
    void this.load();
  }

  private async load() {
    this.errorMsg = "";
    try {
      this.memberships = await myTeams();
    } catch (e) {
      this.errorMsg = humanError(e);
    }
  }

  private async create(e: Event) {
    e.preventDefault();
    const name = this.newName.trim();
    const nameErr = tokenError(name);
    if (nameErr) {
      this.errorMsg = `Team name: ${nameErr}`;
      return;
    }
    this.errorMsg = "";
    this.creating = true;
    try {
      const team = await createTeam(name, this.newDisplay.trim());
      navigate(`/teams/${team.name}`);
    } catch (err) {
      this.errorMsg = humanError(err);
    } finally {
      this.creating = false;
    }
  }

  override render() {
    return html`
      <div class="wrap-narrow">
        <h1>Teams</h1>
        ${this.errorMsg ? html`<div class="error" role="alert">${this.errorMsg}</div>` : ""}
        <div class="card">
          <h2>My teams</h2>
          ${this.memberships.length
            ? html`<div class="table-scroll">
                <table>
                <thead>
                  <tr>
                    <th>Team</th>
                    <th>Your role</th>
                  </tr>
                </thead>
                <tbody>
                  ${this.memberships.map((m) => {
                    const team = m.expand?.team;
                    const name = team?.name ?? m.team;
                    return html`<tr>
                      <td>
                        <a href=${`/teams/${name}`}>${team?.display_name || name}</a>
                        <span class="mono">@${name}</span>
                        ${team?.personal_for ? html`<span class="chip">Personal</span>` : ""}
                      </td>
                      <td><span class="chip">${ROLE_LABELS[m.role] ?? m.role}</span></td>
                    </tr>`;
                  })}
                </tbody>
                </table>
              </div>`
            : html`<p class="muted">You are not a member of any team yet.</p>`}
        </div>
        <div class="card">
          <h2>Create a team</h2>
          <p class="muted">
            The team name becomes its <span class="mono">@scope</span> for packages; the creator
            becomes the team owner.
          </p>
          <form @submit=${this.create}>
            <div class="two">
              <div class="field">
                <label for="team-name">Name</label>
                <input
                  id="team-name"
                  class="mono"
                  placeholder="acme"
                  spellcheck="false"
                  .value=${this.newName}
                  @input=${(e: Event) => (this.newName = (e.target as HTMLInputElement).value)}
                  required
                />
              </div>
              <div class="field">
                <label for="team-display">Display name</label>
                <input
                  id="team-display"
                  placeholder="ACME Corp"
                  .value=${this.newDisplay}
                  @input=${(e: Event) => (this.newDisplay = (e.target as HTMLInputElement).value)}
                />
              </div>
            </div>
            <button class="btn btn-primary" type="submit" ?disabled=${this.creating}>
              ${this.creating ? "Creating…" : "Create team"}
            </button>
          </form>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-teams": PageTeams;
  }
}
